import dynamic from 'next/dynamic'
import Head from 'next/head'
import { services } from '../../queries/services'
import ApiClient from '../../utils/client'
import { useBasket } from '../../providers/basket'
import { useRouter } from 'next/router'
import { serviceTemplate } from '../../utils/schema'
import React, { useState } from 'react'
import { Disclosure } from '@headlessui/react'
import { ChevronUpIcon } from '@heroicons/react/24/solid'

const Layout = dynamic(() => import('../../components/layout'))
const Markdown = dynamic(() => import('../../components/molecules/markdown'))

export default function PriceList(props) {
  const router = useRouter()

  // --- FEATURED SERVICES ---

  // Filter out services with tags
  const specialOffers = props.content.filter((d) =>
    d.tags.some((c) => c.tag.name === 'special-offers')
  )
  const mostPopular = props.content.filter((d) =>
    d.tags.some((c) => c.tag.name === 'most-popular')
  )

  // Build array of objects to map
  let featuredServices = [
    {
      name: 'Most Popular',
      services: mostPopular,
    },
    {
      name: 'Special Offers',
      services: specialOffers,
    },
  ]

  // --- ALL SERVICES WITH SEARCH ---

  function findMatch(array_1_small, array2_large) {
    var ary = new Array()
    for (let i = 0; i < array2_large.length; i++) {
      for (let z = 0; z < array_1_small.length; z++) {
        if (array2_large[i] == array_1_small[z]) {
          ary.push(i)
        }
      }
    }
    return ary
  }

  const categories = [
    ...new Set(props.content.map((service) => service.category.name)),
  ]

  // Set all to found on load
  let defaultFoundService = []
  categories.forEach((x) => defaultFoundService.push('found'))

  const [foundService, setFoundService] = useState(defaultFoundService)
  const [searchValue, setSearchValue] = useState('')
  const [expandOne, setExpandOne] = useState([])

  const { addItem } = useBasket()

  // Make empty array based on length of categories
  let foundArray = []
  categories.forEach((x) => foundArray.push(''))

  function searchServices(e) {
    // Share search value to be used in filter
    setSearchValue(e)

    // Match services
    const found = props.content.filter(
      (service) => service.title.toLowerCase().indexOf(e.toLowerCase()) > -1
    )

    // Categories array of matched services
    const foundCategories = [
      ...new Set(found.map((service) => service.category.name)),
    ]

    // Indexes array of matched services
    const foundIndexes = findMatch(foundCategories, categories)

    // Add "found" to matched position in foundService state
    foundIndexes.forEach((element) => {
      foundArray.splice(element, 0, 'found')
    })
    setFoundService(foundArray)

    // If foundService only contains one, then expand that accordion
    if (foundCategories.length === 1) {
      setExpandOne(foundIndexes)
    } else {
      setExpandOne([])
    }
  }

  function makeBooking(service?, option?, subOption?) {
    addItem(service, undefined, undefined, option, subOption)
    router.push('/booking')
  }

  const meta = props.content.map((x) => serviceTemplate(x))

  return (
    <Layout>
      <Head>
        <title>Price List | Soluna London</title>
        <meta
          name="description"
          content="View the price list of all services at Soluna London."
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: `${JSON.stringify(meta)}` }}
        />
        <link rel="canonical" href={`https://solunalondon.com/price-list/`} />
        <link
          rel="alternate"
          hrefLang="en-GB"
          href={`https://solunalondon.com/price-list/`}
        />
        <link
          rel="alternate"
          hrefLang="en"
          href={`https://solunalondon.com/price-list/`}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`https://solunalondon.com/price-list/`}
        />
      </Head>
      <section className="relative bg-gray-50 pt-16 pb-20 px-4 sm:px-6 lg:pt-24 lg:pb-28 lg:px-8">
        <div className="">
          <div className="bg-white h-1/2 sm:h-2/3"></div>
        </div>
        <div className="relative mb-12 max-w-7xl mx-auto lg:max-w-7xl lg:mx-auto lg:px-8">
          <div className="text-left">
            <h1 className="font-serif">
              <span className="mt-1 block text-4xl font-medium sm:text-5xl xl:text-6xl capitalize">
                <span className="block text-gray-900">
                  Discover all{' '}
                  <span className="text-brand-600">our prices</span>
                </span>
              </span>
            </h1>
          </div>
        </div>

        <div className={`${searchValue.length > 0 && 'hidden'}`}>
          {/* Start of featured services */}
          <div className="mt-12 mb-4 relative max-w-7xl mx-auto lg:max-w-7xl lg:mx-auto lg:px-8">
            <div className="text-left md:grid md:grid-cols-2">
              <h2 className="font-serif md:grid mb-4 md:mb-0">
                <span className="mt-1 block text-3xl font-extrabold sm:text-4xl xl:text-5xl capitalize">
                  <span className="block text-gray-900">Featured services</span>
                </span>
              </h2>
            </div>
          </div>
          <div className="gap-2 max-w-full mx-auto grid sm:max-w-none lg:max-w-7xl lg:mx-auto lg:px-8">
            {/* Special offers */}
            {featuredServices.map((feature, index) => {
              return (
                featuredServices[index].services.length > 0 && (
                  <Disclosure key={feature.toString() + index}>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="flex justify-between w-full px-4 py-4 text-sm font-medium text-left text-white bg-brand-600 rounded-lg hover:bg-brand-800 focus:outline-none focus-visible:ring focus-visible:ring-brand-600 focus-visible:ring-opacity-75">
                          <span>{feature.name}</span>
                          <ChevronUpIcon
                            className={`${open ? 'transform rotate-180' : ''
                              } w-5 h-5 text-white`}
                          />
                        </Disclosure.Button>
                        <Disclosure.Panel unmount={false}>
                          <div className="divide divide-y-2 py-4">
                            {featuredServices[index].services.map(
                              (service, index, array) => {
                                return (
                                  <section
                                    className="p-4"
                                    key={service.category.name + index}
                                  >
                                    <div className="mb-3 md:grid grid-cols-3">
                                      <div className="col-span-2">
                                        <h3 className="font-semibold">
                                          {service.title}
                                        </h3>
                                      </div>
                                      <div className="md:flex md:justify-end">
                                        <a
                                          href={service.metadata.path}
                                          className="pricing-learn-more"
                                        >
                                          Learn more
                                        </a>
                                      </div>
                                    </div>
                                    {service.pricing
                                      .filter(
                                        (option) =>
                                          option.data.label !== 'Booking Fee' &&
                                          option.data.label !==
                                          'Free Consultation'
                                      )
                                      .map((option) => {
                                        const noSubOptions =
                                          option.data.options.length === 0
                                        return (
                                          <React.Fragment
                                            key={option.data.label + index}
                                          >
                                            <div className="grid grid-cols-4 items-center mb-3">
                                              {noSubOptions && (
                                                <>
                                                  <div className="col-span-2">
                                                    <Markdown
                                                      source={option.data.label}
                                                      renderers={{
                                                        ul: function P({
                                                          children,
                                                        }) {
                                                          return (
                                                            <ul className="grid grid-cols-2 text-sm md:text-base">
                                                              {children}
                                                            </ul>
                                                          )
                                                        },
                                                        p: function P({
                                                          children,
                                                        }) {
                                                          return (
                                                            <p className="text-sm md:text-base">
                                                              {children}
                                                            </p>
                                                          )
                                                        },
                                                        li: function LI({
                                                          children,
                                                        }) {
                                                          return (
                                                            <li className="text-sm md:text-base">
                                                              {children}
                                                            </li>
                                                          )
                                                        },
                                                      }}
                                                    ></Markdown>
                                                  </div>
                                                  <div>
                                                    <p className="text-sm md:text-base">{`£${option.data.price / 100
                                                      }`}</p>
                                                    {option.data.was &&
                                                      option.data.was > 0 ? (
                                                      <p className="text-sm md:text-base">
                                                        Was{' '}
                                                        <span
                                                          style={{
                                                            textDecorationColor:
                                                              'red',
                                                            textDecorationLine:
                                                              'line-through',
                                                          }}
                                                        >{`£${option.data.was / 100
                                                          }`}</span>
                                                      </p>
                                                    ) : null}
                                                  </div>
                                                  <div className="flex items-center justify-end">
                                                    <button
                                                      onClick={() =>
                                                        makeBooking(
                                                          array[index],
                                                          option.data.id
                                                        )
                                                      }
                                                      style={{
                                                        padding: '5px 15px',
                                                        fontSize: '14px',
                                                        width: 'max-content',
                                                      }}
                                                      className="btn-primary"
                                                    >
                                                      BOOK
                                                    </button>
                                                  </div>
                                                </>
                                              )}
                                              {!noSubOptions && (
                                                <>
                                                  <div className="col-span-4">
                                                    <Markdown
                                                      source={option.data.label}
                                                      renderers={{
                                                        ul: function P({
                                                          children,
                                                        }) {
                                                          return (
                                                            <ul className="grid grid-cols-2 text-sm md:text-base">
                                                              {children}
                                                            </ul>
                                                          )
                                                        },
                                                        p: function P({
                                                          children,
                                                        }) {
                                                          return (
                                                            <p className="font-semibold text-sm md:text-base">
                                                              {children}
                                                            </p>
                                                          )
                                                        },
                                                        li: function LI({
                                                          children,
                                                        }) {
                                                          return (
                                                            <li className="text-sm md:text-base">
                                                              {children}
                                                            </li>
                                                          )
                                                        },
                                                      }}
                                                    ></Markdown>
                                                  </div>
                                                  {noSubOptions && (
                                                    <div
                                                      className={`grid ${noSubOptions
                                                        ? 'col-span-4'
                                                        : 'col-span-2'
                                                        } grid-cols-2 items-center`}
                                                    >
                                                      <div>
                                                        {noSubOptions && (
                                                          <>
                                                            <p className="text-sm md:text-base">{`£${option.data.price /
                                                              100
                                                              }`}</p>
                                                            {option.data.was &&
                                                              option.data.was >
                                                              0 ? (
                                                              <p className="text-sm md:text-base">
                                                                Was{' '}
                                                                <span
                                                                  style={{
                                                                    textDecorationColor:
                                                                      'red',
                                                                    textDecorationLine:
                                                                      'line-through',
                                                                  }}
                                                                >{`£${option.data
                                                                  .was / 100
                                                                  }`}</span>
                                                              </p>
                                                            ) : null}
                                                          </>
                                                        )}
                                                      </div>
                                                      <div className="flex items-center justify-end">
                                                        <button
                                                          onClick={() =>
                                                            makeBooking(
                                                              array[index],
                                                              option.data.id
                                                            )
                                                          }
                                                          style={{
                                                            padding: '5px 15px',
                                                            fontSize: '14px',
                                                            width: 'max-content',
                                                          }}
                                                          className="btn-primary"
                                                        >
                                                          BOOK
                                                        </button>
                                                      </div>
                                                    </div>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                            {option.data.options.map(
                                              (subOption) => {
                                                return (
                                                  <div
                                                    className="grid grid-cols-12 gap-2 items-center mb-4 odd:bg-gray-300"
                                                    key={
                                                      subOption.data.label + index
                                                    }
                                                  >
                                                    <div className="col-span-6">
                                                      <Markdown
                                                        source={
                                                          subOption.data.label
                                                        }
                                                        renderers={{
                                                          ul: function P({
                                                            children,
                                                          }) {
                                                            return (
                                                              <ul className="text-sm md:text-base grid grid-cols-2">
                                                                {children}
                                                              </ul>
                                                            )
                                                          },
                                                          li: function LI({
                                                            children,
                                                          }) {
                                                            return (
                                                              <li className="text-sm md:text-base">
                                                                {children}
                                                              </li>
                                                            )
                                                          },
                                                          p: function P({
                                                            children,
                                                          }) {
                                                            return (
                                                              <p className="text-sm md:text-base">
                                                                {children}
                                                              </p>
                                                            )
                                                          },
                                                        }}
                                                      ></Markdown>
                                                    </div>
                                                    <div className="grid grid-cols-2 col-span-6 items-center">
                                                      <div>
                                                        <p className="text-sm md:text-base">{`£${subOption.data.price /
                                                          100
                                                          }`}</p>
                                                        {subOption.data.was &&
                                                          subOption.data.was > 0 ? (
                                                          <p className="text-sm md:text-base">
                                                            Was{' '}
                                                            <span
                                                              style={{
                                                                textDecorationColor:
                                                                  'red',
                                                                textDecorationLine:
                                                                  'line-through',
                                                              }}
                                                            >{`£${subOption.data.was /
                                                              100
                                                              }`}</span>
                                                          </p>
                                                        ) : null}
                                                      </div>
                                                      <div className="flex justify-end">
                                                        <button
                                                          onClick={() =>
                                                            makeBooking(
                                                              array[index],
                                                              option.data.id,
                                                              subOption.data.id
                                                            )
                                                          }
                                                          style={{
                                                            padding: '5px 15px',
                                                            fontSize: '14px',
                                                            width: 'max-content',
                                                          }}
                                                          className="btn-primary"
                                                        >
                                                          BOOK
                                                        </button>
                                                      </div>
                                                    </div>
                                                  </div>
                                                )
                                              }
                                            )}
                                          </React.Fragment>
                                        )
                                      })}
                                  </section>
                                )
                              }
                            )}
                          </div>
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                )
              )
            })}
          </div>
        </div>

        {/* Start of all services */}
        <div className="mt-12 mb-4 relative max-w-7xl mx-auto lg:max-w-7xl lg:mx-auto lg:px-8">
          <div className="text-left md:grid md:grid-cols-2">
            <h2 className="font-serif md:grid mb-4 md:mb-0">
              <span className="mt-1 block text-3xl font-extrabold sm:text-4xl xl:text-5xl capitalize">
                <span className="block text-gray-900">All our services</span>
              </span>
            </h2>
            <input
              autoComplete="off"
              placeholder="Search for a service"
              className="md:grid w-full appearance-none relative block border border-gray-300 placeholder-gray-500 rounded-md focus:shadow-outline-brand focus:z-10 focus:outline-none focus:ring-brand-500 focus:border-brand-500 focus:placeholder-gray-400"
              onChange={(e) => searchServices(e.target.value)}
              type="search"
              id="site-search"
              name="q"
              aria-label="Search through site content"
            ></input>
          </div>
        </div>

        <div className="gap-2 max-w-full mx-auto grid sm:max-w-none lg:max-w-7xl lg:mx-auto lg:px-8">
          {searchValue.length > 0 &&
            foundService.filter((x) => x === 'found').length === 0 && (
              <h2 className="text-xl mt-4">{`Could not find any results for "${searchValue}"`}</h2>
            )}
          {categories.map((category, index) => {
            return (
              <Disclosure key={expandOne[0] + category + index}>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex justify-between w-full p-4 text-sm font-medium text-left text-brand-600 bg-gray-200 rounded-lg hover:bg-gray-300 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75">
                      {/* @ts-ignore */}
                      <span>{category}</span>
                      <ChevronUpIcon
                        className={`${open ? 'transform rotate-180' : ''
                          } w-5 h-5 text-brand-700`}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="" unmount={false}>
                      <div className="divide divide-y-2 py-4">
                        {props.content
                          .filter((service) => service.category.name === category)
                          .filter(
                            (service) =>
                              service.title
                                .toLowerCase()
                                .indexOf(searchValue.toLowerCase()) > -1
                          )
                          .map((service, index, array) => {
                            return (
                              <section
                                className="p-4"
                                key={service.category.name + index}
                              >
                                <div className="mb-3 md:grid grid-cols-3">
                                  <div className="col-span-2">
                                    <h3 className="font-semibold">
                                      {service.title}
                                    </h3>
                                  </div>
                                  <div className="md:flex md:justify-end">
                                    <a
                                      href={service.metadata.path}
                                      className="pricing-learn-more"
                                    >
                                      Learn more
                                    </a>
                                  </div>
                                </div>
                                {service.pricing
                                  .filter(
                                    (option) =>
                                      option.data.label !== 'Booking Fee' &&
                                      option.data.label !== 'Free Consultation'
                                  )
                                  .map((option) => {
                                    const noSubOptions =
                                      option.data.options.length === 0

                                    return (
                                      <React.Fragment
                                        key={option.data.label + index}
                                      >
                                        <div className="grid grid-cols-4 items-center mb-3">
                                          {noSubOptions && (
                                            <>
                                              <div id={option.data.id} className="col-span-2">
                                                <Markdown
                                                  source={option.data.label}
                                                  renderers={{
                                                    ul: function P({ children }) {
                                                      return (
                                                        <ul className="grid grid-cols-2 text-sm md:text-base">
                                                          {children}
                                                        </ul>
                                                      )
                                                    },
                                                    p: function P({ children }) {
                                                      return (
                                                        <p className="text-sm md:text-base">
                                                          {children}
                                                        </p>
                                                      )
                                                    },
                                                    li: function LI({
                                                      children,
                                                    }) {
                                                      return (
                                                        <li className="text-sm md:text-base">
                                                          {children}
                                                        </li>
                                                      )
                                                    },
                                                  }}
                                                ></Markdown>
                                              </div>
                                              <div>
                                                <p className="text-sm md:text-base">{`£${option.data.price / 100
                                                  }`}</p>
                                                {option.data.was &&
                                                  option.data.was > 0 ? (
                                                  <p className="text-sm md:text-base">
                                                    Was{' '}
                                                    <span
                                                      style={{
                                                        textDecorationColor:
                                                          'red',
                                                        textDecorationLine:
                                                          'line-through',
                                                      }}
                                                    >{`£${option.data.was / 100
                                                      }`}</span>
                                                  </p>
                                                ) : null}
                                              </div>
                                              <div className="flex items-center justify-end">
                                                <button
                                                  onClick={() =>
                                                    makeBooking(
                                                      array[index],
                                                      option.data.id
                                                    )
                                                  }
                                                  style={{
                                                    padding: '5px 15px',
                                                    fontSize: '14px',
                                                    width: 'max-content',
                                                  }}
                                                  className="btn-primary"
                                                >
                                                  BOOK
                                                </button>
                                              </div>
                                            </>
                                          )}
                                          {!noSubOptions && (
                                            <>
                                              <div id={option.data.id} className="col-span-4">
                                                <Markdown
                                                  source={option.data.label}
                                                  renderers={{
                                                    ul: function P({ children }) {
                                                      return (
                                                        <ul className="grid grid-cols-2 text-sm md:text-base">
                                                          {children}
                                                        </ul>
                                                      )
                                                    },
                                                    p: function P({ children }) {
                                                      return (
                                                        <p className="font-semibold text-sm md:text-base">
                                                          {children}
                                                        </p>
                                                      )
                                                    },
                                                    li: function LI({
                                                      children,
                                                    }) {
                                                      return (
                                                        <li className="text-sm md:text-base">
                                                          {children}
                                                        </li>
                                                      )
                                                    },
                                                  }}
                                                ></Markdown>
                                              </div>
                                              {noSubOptions && (
                                                <div
                                                  className={`grid ${noSubOptions
                                                    ? 'col-span-4'
                                                    : 'col-span-2'
                                                    } grid-cols-2 items-center`}
                                                >
                                                  <div>
                                                    {noSubOptions && (
                                                      <>
                                                        <p className="text-sm md:text-base">{`£${option.data.price / 100
                                                          }`}</p>
                                                        {option.data.was &&
                                                          option.data.was > 0 ? (
                                                          <p className="text-sm md:text-base">
                                                            Was{' '}
                                                            <span
                                                              style={{
                                                                textDecorationColor:
                                                                  'red',
                                                                textDecorationLine:
                                                                  'line-through',
                                                              }}
                                                            >{`£${option.data.was /
                                                              100
                                                              }`}</span>
                                                          </p>
                                                        ) : null}
                                                      </>
                                                    )}
                                                  </div>
                                                  <div className="flex items-center justify-end">
                                                    <button
                                                      onClick={() =>
                                                        makeBooking(
                                                          array[index],
                                                          option.data.id
                                                        )
                                                      }
                                                      style={{
                                                        padding: '5px 15px',
                                                        fontSize: '14px',
                                                        width: 'max-content',
                                                      }}
                                                      className="btn-primary"
                                                    >
                                                      BOOK
                                                    </button>
                                                  </div>
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </div>
                                        {option.data.options.map((subOption) => {
                                          return (
                                            <div
                                              id={subOption.data.id}
                                              className="grid grid-cols-12 gap-2 items-center mb-4 odd:bg-gray-300"
                                              key={subOption.data.label + index}
                                            >
                                              <div className="col-span-6">
                                                <Markdown
                                                  source={subOption.data.label}
                                                  renderers={{
                                                    ul: function P({ children }) {
                                                      return (
                                                        <ul className="text-sm md:text-base grid grid-cols-2">
                                                          {children}
                                                        </ul>
                                                      )
                                                    },
                                                    li: function LI({
                                                      children,
                                                    }) {
                                                      return (
                                                        <li className="text-sm md:text-base">
                                                          {children}
                                                        </li>
                                                      )
                                                    },
                                                    p: function P({ children }) {
                                                      return (
                                                        <p className="text-sm md:text-base">
                                                          {children}
                                                        </p>
                                                      )
                                                    },
                                                  }}
                                                ></Markdown>
                                              </div>
                                              <div className="grid grid-cols-2 col-span-6 items-center">
                                                <div>
                                                  <p className="text-sm md:text-base">{`£${subOption.data.price / 100
                                                    }`}</p>
                                                  {subOption.data.was &&
                                                    subOption.data.was > 0 ? (
                                                    <p className="text-sm md:text-base">
                                                      Was{' '}
                                                      <span
                                                        style={{
                                                          textDecorationColor:
                                                            'red',
                                                          textDecorationLine:
                                                            'line-through',
                                                        }}
                                                      >{`£${subOption.data.was / 100
                                                        }`}</span>
                                                    </p>
                                                  ) : null}
                                                </div>
                                                <div className="flex justify-end">
                                                  <button
                                                    onClick={() =>
                                                      makeBooking(
                                                        array[index],
                                                        option.data.id,
                                                        subOption.data.id
                                                      )
                                                    }
                                                    style={{
                                                      padding: '5px 15px',
                                                      fontSize: '14px',
                                                      width: 'max-content',
                                                    }}
                                                    className="btn-primary"
                                                  >
                                                    BOOK
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          )
                                        })}
                                      </React.Fragment>
                                    )
                                  })}
                              </section>
                            )
                          })}
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            )
          })}
        </div>
      </section>
    </Layout>
  )
}

export async function getStaticProps() {
  const client = ApiClient()

  const pageData = await client.query({
    query: services,
  })

  return {
    props: {
      ...pageData.data,
    },
  }
}
